import {
  RoomInfoProducts,
  RoomProductType,
  RoomProduct,
  RoomInfoProductsWithTransformedIndexes,
  RoomProductWithTransformedIndexes,
  DiscountAwarePrices,
  HotelCancellationPolicyV2,
  CancellationPolicyScenarioEnum,
  HotelCancellationPolicyV2Enum,
  ICancellationPolicyScenario,
  Lodging,
  LodgingComparePrice,
  LodgingCollectionEnum,
  StayType,
  TabViewEnum,
  HotelStarRatingEnum,
} from "./interfaces";
import {
  HotelCfarQuote,
  HotelCfarQuoteAdditionalInfo,
  RoomInfoCfarQuote,
  CfarQuoteEnum,
  CfarQuoteResultEnum,
} from "../apis/tysons";
import { Prices } from "../common";

/**
 * Reads the BFF `promotionKind` signal (`MemberOnlyDeal` / `NthNight`) off the pricing object.
 * The field isn't on this repo's hand-authored types (only the embedded @capone/portal bundle
 * references it), so it's read structurally via `in` — mirroring b2bportal's getPromotionKind.
 */
export const getPromotionKind = (
  ...sources: (object | null | undefined)[]
): "MemberOnlyDeal" | "NthNight" | undefined => {
  for (const source of sources) {
    if (source != null && "promotionKind" in source) {
      const kind = (source as { promotionKind?: unknown }).promotionKind;
      if (kind === "MemberOnlyDeal" || kind === "NthNight") {
        return kind;
      }
    }
  }
  return undefined;
};

/**
 * Promotion analytics props mirroring b2bportal's getAvailabilityPromotionTracking, keyed off
 * the `promotionKind` signal. `promotion_amount` is the strikethrough savings:
 * - MemberOnlyDeal: `priceWithNoDiscounts − priceWithUnmanagedDiscounts` on the total
 *   discount-aware prices (the taxes/fees cancel vs. the reconstructed strikethrough).
 * - NthNight: `nthNightPromotion.originalPriceTotal − totalPrice`.
 * Omitted when not derivable / not positive; the two optional keys are omitted when no
 * promotion applies.
 */
export const getPromotionTrackingProperties = (
  lodging: Lodging | null | undefined
): {
  promotion_type?: "MemberOnlyDeal" | "NthNight";
  promotion_amount?: number;
} => {
  const price = lodging?.price;
  const promotion_type = getPromotionKind(price);
  if (price == null || promotion_type == null) {
    return {};
  }

  let promotion_amount: number | undefined;
  if (promotion_type === "NthNight") {
    const original = lodging?.nthNightPromotion?.originalPriceTotal?.fiat.value;
    const promoted = price.totalPrice?.fiat.value;
    if (original != null && promoted != null && original - promoted > 0) {
      promotion_amount = original - promoted;
    }
  } else {
    const noDiscount =
      price.totalDiscountAware?.priceWithNoDiscounts.fiat.value;
    const withDiscount =
      price.totalDiscountAware?.priceWithUnmanagedDiscounts.fiat.value;
    if (
      noDiscount != null &&
      withDiscount != null &&
      noDiscount - withDiscount > 0
    ) {
      promotion_amount = noDiscount - withDiscount;
    }
  }

  return { promotion_type, promotion_amount };
};

export interface ISearchCfarResultByIndex {
  roomInfoIndex: number;
  productIndex: number;
}

export interface ISearchCfarQuoteByIndex extends ISearchCfarResultByIndex { }

export interface ISearchCancellationPolicyByIndex
  extends ISearchCfarResultByIndex { }

export interface ISearchAdditionalInfoByIndex
  extends ISearchCfarResultByIndex { }

export const getCancellationPolicyScenarioEnumByRoomIndex = ({
  roomIndex,
  roomInfoProducts,
  getHotelAdditionalInfoByIndex,
  isRefundableRoomsWithPartiallyRefundablePolicyEnabled = false,
}: {
  roomIndex: number;
  roomInfoProducts: RoomInfoProducts[];
  getHotelAdditionalInfoByIndex: (
    props: ISearchAdditionalInfoByIndex
  ) => HotelCfarQuoteAdditionalInfo | null;
  isRefundableRoomsWithPartiallyRefundablePolicyEnabled?: boolean;
}): CancellationPolicyScenarioEnum => {
  if (!isRefundableRoomsWithPartiallyRefundablePolicyEnabled) {
    return CancellationPolicyScenarioEnum.Other;
  }

  let hasOfferWithCfarPremiumRefunded = false;
  let hasOfferWithoutCfarPremiumRefunded = false;

  const products: RoomProduct[] | undefined =
    roomInfoProducts[roomIndex]?.products;
  products?.forEach((_, productIndex) => {
    const additionalInfo = getHotelAdditionalInfoByIndex({
      roomInfoIndex: roomIndex,
      productIndex,
    });
    if (!!additionalInfo?.shouldRefundCfarPremium) {
      hasOfferWithCfarPremiumRefunded = true;
    } else {
      hasOfferWithoutCfarPremiumRefunded = true;
    }
  });

  if (hasOfferWithCfarPremiumRefunded && hasOfferWithoutCfarPremiumRefunded) {
    return CancellationPolicyScenarioEnum.TwoHotelOptionsOneNonRefOnePartiallyRef;
  } else {
    return CancellationPolicyScenarioEnum.Other;
  }
};

export const getShopRoomInfoProductsWithTransformedIndexes = ({
  roomInfoProducts,
  getHotelCfarQuoteByIndex,
  cfarQuotes,
  getHotelCancellationPolicyByIndex,
  getHotelAdditionalInfoByIndex,
  nightCount,
  pricesHelpers,
  isRefundableRoomsWithPartiallyRefundablePolicyEnabled = false,
  isHotelCfarRrMultipleCancellationPoliciesEnabled = false,
  isHotelCfarModelV1Enabled,
  shouldDisableRefundableRooms,
  matchCfarQuotesWithRoomId,
  displayTaxesAndFeesIncluded,
  displayTaxesAndFeesIncludedNightlyPrice,
}: {
  roomInfoProducts: RoomInfoProducts[];
  getHotelCfarQuoteByIndex: (
    props: ISearchCfarQuoteByIndex
  ) => HotelCfarQuote | null;
  cfarQuotes?: RoomInfoCfarQuote[];
  getHotelCancellationPolicyByIndex: (
    props: ISearchCancellationPolicyByIndex
  ) => HotelCancellationPolicyV2 | null;
  getHotelAdditionalInfoByIndex: (
    props: ISearchAdditionalInfoByIndex
  ) => HotelCfarQuoteAdditionalInfo | null;
  nightCount: number | null;
  pricesHelpers: {
    getModifiedLegacyPrices: (
      basePrices: Prices,
      applyModifier: (value: number) => number
    ) => Prices;
    getAddedLegacyPrices: (basePrices: Prices, addedPrices: Prices) => Prices;
  };
  isRefundableRoomsWithPartiallyRefundablePolicyEnabled?: boolean;
  isHotelCfarRrMultipleCancellationPoliciesEnabled?: boolean;
  isHotelCfarModelV1Enabled?: boolean;
  shouldDisableRefundableRooms?: boolean;
  // todo rm this conditional
  matchCfarQuotesWithRoomId?: boolean;
  displayTaxesAndFeesIncluded?: boolean;
  displayTaxesAndFeesIncludedNightlyPrice?: boolean;
}): RoomInfoProductsWithTransformedIndexes[] => {
  const { getModifiedLegacyPrices, getAddedLegacyPrices } = pricesHelpers;
  const result: RoomInfoProductsWithTransformedIndexes[] = [];
  roomInfoProducts.forEach((roomInfoProduct, roomIndex) => {
    const products: RoomProductWithTransformedIndexes[] = [];
    const cancellationPolicyScenarioEnum =
      getCancellationPolicyScenarioEnumByRoomIndex({
        roomIndex,
        roomInfoProducts,
        getHotelAdditionalInfoByIndex,
        isRefundableRoomsWithPartiallyRefundablePolicyEnabled,
      });
    const hotelRoomId = roomInfoProduct.roomInfo.roomId;
    // the cfar quotes for this room
    const roomAssociatedCfarQuotes = cfarQuotes?.find(
      (cfarQuote) => cfarQuote.roomInfo.roomId === hotelRoomId
    );

    const getCancellationPolicyScenarioEnumByRoomId = () => {
      let hasOfferWithCfarPremiumRefunded = false;
      let hasOfferWithoutCfarPremiumRefunded = false;
      roomAssociatedCfarQuotes?.cfarQuotes.forEach((quote) => {
        if (quote.CfarQuote === CfarQuoteEnum.HotelCfarEligible) {
          const additionalInfo = quote.additionalInfo;
          if (additionalInfo.shouldRefundCfarPremium) {
            hasOfferWithCfarPremiumRefunded = true;
          } else {
            hasOfferWithoutCfarPremiumRefunded = true;
          }
        }
      });
      // replicating the logic of getCancellationPolicyScenarioEnumByRoomIndex except
      // identifying the cfar quotes based off room id
      const cancellationPolicyScenarioEnumByRoomId =
        !isRefundableRoomsWithPartiallyRefundablePolicyEnabled
          ? CancellationPolicyScenarioEnum.Other
          : hasOfferWithCfarPremiumRefunded &&
            hasOfferWithoutCfarPremiumRefunded
            ? CancellationPolicyScenarioEnum.TwoHotelOptionsOneNonRefOnePartiallyRef
            : CancellationPolicyScenarioEnum.Other;
      return cancellationPolicyScenarioEnumByRoomId;
    };

    let hasAddedRefundableRoom = false;
    let hasDisplayedNonrefundable = false;

    roomInfoProduct.products.forEach((roomProduct, productIndex) => {
      const baseProduct = {
        roomIndex,
        productIndex,
        rateId: roomProduct.rateId,
      };

      products.push({
        ...roomProduct,
        roomProductVariant: {
          baseProduct,
          variant: RoomProductType.Default,
        },
      });

      // refundable room
      const associatedProductCfarQuote =
        roomAssociatedCfarQuotes?.cfarQuotes.find((quote) => {
          if (quote.CfarQuote === CfarQuoteEnum.HotelCfarEligible) {
            return quote.rateId?.value === roomProduct.rateId?.value;
          }
          return false;
        });
      const cfarQuote = getHotelCfarQuoteByIndex({
        roomInfoIndex: roomIndex,
        productIndex,
      });

      const getCfarQuoteByRoomId = () => {
        if (
          associatedProductCfarQuote?.CfarQuote ===
          CfarQuoteEnum.HotelCfarEligible
        ) {
          if (
            associatedProductCfarQuote.cfarQuoteResult.CfarQuoteResult ===
            CfarQuoteResultEnum.HotelCfarQuote
          ) {
            return associatedProductCfarQuote.cfarQuoteResult;
          }
        }
        return null;
      };

      const additionalInfo = getHotelAdditionalInfoByIndex({
        roomInfoIndex: roomIndex,
        productIndex,
      });

      const getAdditionalInfoByRoomId = () =>
        associatedProductCfarQuote?.CfarQuote ===
          CfarQuoteEnum.HotelCfarEligible
          ? associatedProductCfarQuote.additionalInfo
          : null;

      const cancellationPolicy = getHotelCancellationPolicyByIndex({
        roomInfoIndex: roomIndex,
        productIndex,
      });

      const getCfarCancellationPolicyByRoomId = () =>
        associatedProductCfarQuote?.CfarQuote ===
          CfarQuoteEnum.HotelCfarEligible
          ? associatedProductCfarQuote.cancellationPolicyV2
          : null;

      const {
        validCfarQuote,
        validCancellationPolicy,
        validAdditionalInfo,
        validCancellationPolicyEnum,
      } = {
        validCfarQuote: matchCfarQuotesWithRoomId
          ? getCfarQuoteByRoomId()
          : cfarQuote,
        validCancellationPolicy: matchCfarQuotesWithRoomId
          ? getCfarCancellationPolicyByRoomId()
          : cancellationPolicy,
        validAdditionalInfo: matchCfarQuotesWithRoomId
          ? getAdditionalInfoByRoomId()
          : additionalInfo,
        validCancellationPolicyEnum: matchCfarQuotesWithRoomId
          ? getCancellationPolicyScenarioEnumByRoomId()
          : cancellationPolicyScenarioEnum,
      };

      const cancellationPolicyScenario: ICancellationPolicyScenario = {
        type: validCancellationPolicyEnum,
      };
      const isNonRefundable =
        validCancellationPolicy?.CancellationPolicyV2 ===
        HotelCancellationPolicyV2Enum.NonRefundable;
      /*
        note: if there is a non-refundable and partially refundable option by the hotel the user will see a
        “Fully refundable by Capital One Travel until {X date}” that's built off the hotel’s partially refundable rate

        for more details, see https://hopper-jira.atlassian.net/browse/BF-2241

        also used for https://hopper-jira.atlassian.net/browse/BF-2274
      */
      const isTwoHotelOptionsConditionSatisfied =
        cancellationPolicyScenario.type ===
        CancellationPolicyScenarioEnum.Other ||
        (cancellationPolicyScenario.type ===
          CancellationPolicyScenarioEnum.TwoHotelOptionsOneNonRefOnePartiallyRef &&
          /*
            note: rule 5 - if there is a non-refundable and partially refundable option by the hotel, the user should not
            get a refundable room rate https://hopper-jira.atlassian.net/browse/BF-2274
          */
          !isHotelCfarRrMultipleCancellationPoliciesEnabled &&
          !!validAdditionalInfo?.shouldRefundCfarPremium);

      // If the flag isRefundableByCapitalOne is returned on the cfarQuote, we will
      // use that quote to create the "Refundable By Capital One Travel" rate, bypassing
      // the business logic implemented here on the FE
      // see https://hopper-jira.atlassian.net/browse/FLEX-3576 for more context
      const addCfarQuote = ({
        overrideFilteringLogic,
        isHotelCfarModelV1Enabled,
      }: {
        overrideFilteringLogic?: boolean;
        isHotelCfarModelV1Enabled?: boolean;
      }) => {
        if (!validCfarQuote) {
          return;
        }

        const {
          id: quoteId,
          premiumPrices,
          coverageAmount,
          coveragePercentage,
          pricingDescription,
        } = validCfarQuote;

        const totalDiscountAware: DiscountAwarePrices | undefined =
          roomProduct?.totalDiscountAware
            ? {
              ...roomProduct.totalDiscountAware,
              priceWithUnmanagedDiscounts: getAddedLegacyPrices(
                roomProduct.totalDiscountAware.priceWithUnmanagedDiscounts,
                premiumPrices
              ),
            }
            : undefined;

        const sellRate = getAddedLegacyPrices(
          roomProduct.sellRate,
          premiumPrices
        );

        const premiumPricesPerNight = getModifiedLegacyPrices(
          premiumPrices,
          (total: number) => Math.ceil(total / (nightCount ?? 1)) // Rounding here for consistency so we don't rely on the implementation of other modules
        );

        const addQuote = () => {
          products.push({
            ...roomProduct,
            // total
            ...(displayTaxesAndFeesIncluded
              ? {
                tripTotal: getAddedLegacyPrices(
                  roomProduct.tripTotal,
                  premiumPrices
                ),
                total: getAddedLegacyPrices(roomProduct.total, premiumPrices),
              }
              : undefined),
            ...(totalDiscountAware ? { totalDiscountAware } : undefined),
            sellRate,
            // per night
            ...(displayTaxesAndFeesIncludedNightlyPrice &&
              roomProduct?.nightlyDiscountAwareTaxesAndFeesIncluded
              ? {
                nightlyDiscountAware: {
                  ...roomProduct?.nightlyDiscountAwareTaxesAndFeesIncluded,
                  priceWithUnmanagedDiscounts: getAddedLegacyPrices(
                    roomProduct.nightlyDiscountAwareTaxesAndFeesIncluded
                      .priceWithUnmanagedDiscounts,
                    premiumPricesPerNight
                  ),
                },
              }
              : roomProduct?.nightlyDiscountAware
                ? {
                  nightlyDiscountAware: {
                    ...roomProduct.nightlyDiscountAware,
                    priceWithUnmanagedDiscounts: getAddedLegacyPrices(
                      roomProduct.nightlyDiscountAware
                        .priceWithUnmanagedDiscounts,
                      premiumPricesPerNight
                    ),
                  },
                }
                : undefined),
            perNightSellRate:
              displayTaxesAndFeesIncludedNightlyPrice &&
                roomProduct?.perNightSellRateTaxesAndFeesIncluded
                ? getAddedLegacyPrices(
                  roomProduct.perNightSellRateTaxesAndFeesIncluded,
                  premiumPricesPerNight
                )
                : getAddedLegacyPrices(
                  roomProduct.perNightSellRate,
                  premiumPricesPerNight
                ),
            perNightSellRateTaxesAndFeesIncluded:
              roomProduct.perNightSellRateTaxesAndFeesIncluded
                ? getAddedLegacyPrices(
                  roomProduct.perNightSellRateTaxesAndFeesIncluded,
                  premiumPricesPerNight
                )
                : roomProduct.perNightSellRateTaxesAndFeesIncluded,
            nightlyDiscountAwareTaxesAndFeesIncluded:
              roomProduct.nightlyDiscountAwareTaxesAndFeesIncluded
                ? Object.keys(
                  roomProduct.nightlyDiscountAwareTaxesAndFeesIncluded
                ).reduce((acc, curr) => {
                  acc[curr] = getAddedLegacyPrices(
                    roomProduct.nightlyDiscountAwareTaxesAndFeesIncluded![
                    curr
                    ],
                    premiumPricesPerNight
                  );
                  return acc;
                }, {} as DiscountAwarePrices)
                : roomProduct.nightlyDiscountAwareTaxesAndFeesIncluded,
            tripTotalExcludingTaxes: roomProduct.tripTotalExcludingTaxes
              ? getAddedLegacyPrices(
                roomProduct.tripTotalExcludingTaxes,
                // Issue with the types from redmond and b2b-interfaces
                premiumPrices as Prices
              )
              : roomProduct.tripTotalExcludingTaxes,
            roomProductVariant: {
              premiumPrices,
              premiumPricesPerNight,
              quoteId,
              coveragePercentage,
              coverageAmount,
              baseProduct,
              pricingDescription,
              additionalInfo: validAdditionalInfo,
              cancellationPolicy: validCancellationPolicy,
              cancellationPolicyScenario,
              variant: RoomProductType.RefundableRoom,
            },
          });
          hasAddedRefundableRoom = true;
        };

        if (isHotelCfarModelV1Enabled) {
          if (overrideFilteringLogic) {
            addQuote();
          }
          return;
        }

        if (
          /*
          note: rule 1 - it adds the filtering rule to show the rate with the highest value only;
          see https://hopper-jira.atlassian.net/browse/BF-1407
        */
          !hasAddedRefundableRoom &&
          // note: rule 3 - address multiple nonrefundable room products; see https://hopper-jira.atlassian.net/browse/BF-1716
          !(hasDisplayedNonrefundable && isNonRefundable) &&
          /*
          note: rule 4 - if there is a non-refundable and partially refundable option by the hotel the user will see
          a “Fully refundable by Capital One Travel until {X date}” that's built off the hotel’s partially refundable rate
        */
          isTwoHotelOptionsConditionSatisfied
        ) {
          const nextProductIndex = productIndex + 1;
          const nextProduct = roomInfoProduct.products[nextProductIndex];
          const isMoreExpensiveThanNextProduct =
            nextProductIndex < roomInfoProduct.products.length
              ? (totalDiscountAware?.priceWithUnmanagedDiscounts.fiat.value ??
                sellRate.fiat.value) >
              (nextProduct.totalDiscountAware?.priceWithUnmanagedDiscounts
                .fiat.value ?? nextProduct.sellRate.fiat.value)
              : false;
          /*
          note: rule 2 - don't show made refundable room by capital one when more expensive than refundable rate;
          see https://hopper-jira.atlassian.net/browse/BF-1691
          */
          if (!isMoreExpensiveThanNextProduct) {
            addQuote();
          }
        }
      };

      // we want to add the ability to hide RR, but not standalone CFAR for certain hotels
      // this flag comes from Tysons and is set at the hotel level
      if (!shouldDisableRefundableRooms) {
        addCfarQuote({
          overrideFilteringLogic: validAdditionalInfo?.isRefundableByCapitalOne,
          isHotelCfarModelV1Enabled,
        });

        if (isNonRefundable) {
          hasDisplayedNonrefundable = true;
        }
      }
    });

    result.push({
      ...roomInfoProduct,
      products,
    });
  });
  return result;
};

export const getNthNightTrackingProperty = (lodgingData: Lodging | null) =>
  lodgingData?.nthNightPromotion
    ? lodgingData.nthNightPromotion.isLocked
      ? "locked"
      : "unlocked"
    : "none";

export const getProductTypeTrackingProperty = (lodgingData: Lodging | null) => {
  const collection = lodgingData?.lodgingCollection;
  return collection === LodgingCollectionEnum.Premier ||
    collection === LodgingCollectionEnum.Lifestyle
    ? (collection as string)
    : "";
};

export const getStaysTabTrackingProperty = (staysTypes: StayType[]) => {
  const stayType = staysTypes[0];
  switch (stayType) {
    case StayType.Hotels:
      return TabViewEnum.HOTEL;
    case StayType.Homes:
      return TabViewEnum.VACATION_RENTAL;
    default:
      return staysTypes.length === 0 ? TabViewEnum.ALL_STAYS : "";
  }
};

export const getLodgingTrackingProperties = (lodgingData: Lodging | null) => {
  if (!lodgingData) {
    return { properties: {}, encryptedProperties: [] };
  }
  let comparableRate: LodgingComparePrice | null = null;
  if (
    lodgingData.price?.comparePrices &&
    lodgingData.price?.comparePrices.length > 0
  ) {
    comparableRate = lodgingData.price.comparePrices[0];
  }

  return {
    properties: {
      metadata_provider: lodgingData.price?.providerName || "N/A",
      metadata_count: lodgingData.lodging.media.length || 0,
      is_pc: lodgingData.lodgingCollection === LodgingCollectionEnum.Premier,
      promotion_applicable:
        lodgingData?.bestPromotionThisLodging?.promotionType,
      promotion_discount:
        lodgingData?.bestPromotionThisLodging?.discountAmount.amount,
      promotion_discount_currency:
        lodgingData?.bestPromotionThisLodging?.discountAmount.currency,
      promotion_discount_percentage:
        lodgingData?.bestPromotionThisLodging?.discountPercentage,
      lowest_comparable_daily_including_taxes_fees_usd:
        comparableRate?.nightlyPrice.fiat.value,
      lowest_comparable_daily_including_taxes_fees_user_currency:
        comparableRate?.nightlyPrice.fiat.currencyCode,
      lowest_comparable_provider: comparableRate?.source,
      provider_name: lodgingData.price?.providerName,
      rate_provider: lodgingData.price?.providerName,
      ...lodgingData?.trackingPropertiesV2?.properties,
      ...lodgingData?.bestOfferThisLodging?.trackingPropertiesV2?.properties,
      has_offer: !!lodgingData?.bestOfferThisLodging,
      is_lc: lodgingData.lodgingCollection === LodgingCollectionEnum.Lifestyle,
      loyalty_program_code: lodgingData?.loyaltyProgramCode,
      nth_night_promo: getNthNightTrackingProperty(lodgingData),
      meal_plan: lodgingData.price?.mealPlan?.kind,
      lodging_tripadvisor_rating:
        lodgingData?.lodging.tripAdvisorReviews?.overallScore.toString(),
      product_type: getProductTypeTrackingProperty(lodgingData),
      is_corporate: lodgingData?.hasCorporateRates,
    },
    encryptedProperties: [
      lodgingData?.trackingPropertiesV2?.encryptedProperties ?? "",
      lodgingData?.bestOfferThisLodging?.trackingPropertiesV2
        ?.encryptedProperties ?? "",
    ],
  };
};

// Persistent lodging tracking properties are used to carry over specific tracking properties in use cases
// where the redux state may re-initialize, e.g. during page redirects.
export interface IPersistentLodgingTrackingProperties {
  availability_id?: string;
  pricing_engine_pricing_id?: string;
  is_boosted?: boolean;
}

// Helper function to build encoded persistent lodging tracking properties from the lodging object.
export const buildEncodedPersistentLodgingTrackingProperties = (
  lodgingData: Lodging | null
): string | null => {
  if (!lodgingData) {
    return null;
  }
  return encodePersistentLodgingTrackingProperties(
    getPersistentLodgingTrackingProperties(lodgingData)
  );
};

export const getPersistentLodgingTrackingProperties = (
  lodgingData: Lodging | null
): IPersistentLodgingTrackingProperties | null => {
  if (!lodgingData) {
    return null;
  }
  return {
    ...(lodgingData?.trackingPropertiesV2?.properties?.is_boosted && {
      is_boosted: lodgingData?.trackingPropertiesV2?.properties?.is_boosted,
    }),
    ...(lodgingData?.trackingPropertiesV2?.properties?.availability_id && {
      availability_id:
        lodgingData?.trackingPropertiesV2?.properties?.availability_id,
    }),
    ...(lodgingData?.trackingPropertiesV2?.properties
      ?.pricing_engine_pricing_id && {
      pricing_engine_pricing_id:
        lodgingData?.trackingPropertiesV2?.properties
          ?.pricing_engine_pricing_id,
    }),
  };
};

export const encodePersistentLodgingTrackingProperties = (
  trackingProperties: IPersistentLodgingTrackingProperties | null
): string | null => {
  if (!trackingProperties) {
    return null;
  }
  return encodeURIComponent(JSON.stringify(trackingProperties));
};

export const decodePersistentLodgingTrackingProperties = (
  encodedProperties: string
): IPersistentLodgingTrackingProperties | null => {
  try {
    const decodedProperties = decodeURIComponent(encodedProperties);
    const parsedProperties = JSON.parse(decodedProperties);
    return {
      ...(parsedProperties.availability_id && {
        availability_id: parsedProperties.availability_id,
      }),
      ...(parsedProperties.pricing_engine_pricing_id && {
        pricing_engine_pricing_id: parsedProperties.pricing_engine_pricing_id,
      }),
      ...(parsedProperties.is_boosted && {
        is_boosted: parsedProperties.is_boosted,
      }),
    };
  } catch {
    return null;
  }
};

export const MILES = "Miles";
export const CASH = "Cash";
export const POINTS = "Points";

export type RewardsCurrency = typeof MILES | typeof CASH | typeof POINTS;
export const ValidRewardsCurrency = [MILES, CASH, POINTS] as const;

interface Multiplier {
  currency: RewardsCurrency;
  multiplier: number;
}

export const RewardsMultipliers: Record<RewardsCurrency, Multiplier> = {
  Miles: {
    currency: MILES,
    multiplier: 100,
  },
  Cash: {
    currency: CASH,
    multiplier: 1,
  },
  Points: {
    currency: POINTS,
    multiplier: 100,
  }
};

export function hotelStarRatingToNumber(
  starRating: HotelStarRatingEnum
): number {
  switch (starRating) {
    case HotelStarRatingEnum.NotAvailable:
      return 0;
    case HotelStarRatingEnum.One:
      return 1;
    case HotelStarRatingEnum.Two:
      return 2;
    case HotelStarRatingEnum.Three:
      return 3;
    case HotelStarRatingEnum.Four:
      return 4;
    case HotelStarRatingEnum.Five:
      return 5;
    default:
      return 0;
  }
}
